import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import { Client } from '../types';
import Table from '../components/ui/Table';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StatsDashboard from '../components/StatsDashboard';

// Composant Modal simple pour la confirmation
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}> = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {message}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onConfirm}
            >
              Supprimer
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fonction pour charger les clients
  const fetchClients = async () => {
    try {
      setIsLoading(true);
      const data = await clientService.getAllClients();
      setClients(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des clients');
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les clients au chargement de la page
  useEffect(() => {
    fetchClients();
  }, []);
  
  // Fonction pour ouvrir le modal de confirmation de suppression
  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteModalOpen(true);
  };
  
  // Fonction pour fermer le modal de confirmation
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setClientToDelete(null);
  };
  
  // Fonction pour confirmer la suppression d'un client
  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;
    
    try {
      setIsLoading(true);
      await clientService.deleteClient(clientToDelete.id);
      
      // Mettre à jour la liste des clients
      setClients(clients.filter(client => client.id !== clientToDelete.id));
      setSuccessMessage(`Le client ${clientToDelete.name} a été supprimé avec succès`);
      
      // Fermer le modal
      setDeleteModalOpen(false);
      setClientToDelete(null);
      
      // Effacer le message de succès après 5 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression du client');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction pour naviguer vers la page de modification d'un client
  const handleEditClick = (client: Client) => {
    navigate(`/clients/${client.id}/edit`);
  };

  const columns: Array<{
    header: string;
    accessor: keyof Client | ((client: Client) => React.ReactNode);
    className?: string;
  }> = [
    {
      header: 'Nom',
      accessor: 'name' as keyof Client,
    },
    {
      header: 'Sous-domaine',
      accessor: (client: Client) => (
        <span className="font-medium text-blue-600">{client.subdomain}.api100.io</span>
      ),
    },
    {
      header: 'Statut',
      accessor: (client: Client) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            client.status === 'online'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {client.status === 'online' ? 'En ligne' : 'Hors ligne'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: (client: Client) => (
        <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
          <Link to={`/clients/${client.id}`} title="Détails">
            <Button size="icon" variant="primary">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Button>
          </Link>
          <Button 
            size="icon" 
            variant="secondary" 
            title="Modifier"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(client);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Button>
          <Button 
            size="icon" 
            variant="danger" 
            title="Supprimer"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(client);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </Button>
          <a href={clientService.getAgentDownloadUrl(client.id)} download onClick={(e) => e.stopPropagation()} title="Télécharger l'agent">
            <Button size="icon" variant="outline">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </Button>
          </a>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Tableau de bord</h1>
          <Link to="/clients/new">
            <Button variant="primary">Nouveau client</Button>
          </Link>
        </div>

        {successMessage && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tableau de bord des statistiques */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Statistiques globales</h2>
          <StatsDashboard />
        </div>
        
        {/* Liste des clients */}
        <Card>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Vos clients</h2>
          <Table
            columns={columns}
            data={clients}
            keyExtractor={(client) => client.id}
            isLoading={isLoading}
            emptyMessage="Aucun client trouvé. Créez votre premier client pour commencer."
            onRowClick={(client) => {
              // Rediriger vers la page de détails du client seulement si on clique sur la ligne
              // et non sur les boutons d'action
              navigate(`/clients/${client.id}`);
            }}
          />
        </Card>
      </div>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Supprimer le client"
        message={clientToDelete ? `Êtes-vous sûr de vouloir supprimer le client ${clientToDelete.name} ? Cette action est irréversible.` : ''}
      />
    </div>
  );
};

export default Dashboard;
